/**
 * Compass agent tools registration.
 *
 * Exposes the compliance / evidence / audit-export services to the
 * chat assistant. Per `docs/core/agent-tool-architecture.md`:
 *   - safe tools: read-only (compliance_evaluate, compliance_gaps,
 *     find_risks, flag_control_gaps, export_audit_bundle returns URL)
 *   - structural tools: mutate persisted state with preview/confirm
 *     (attach_evidence, recommend_remediation)
 *
 * Tools depend on `assistantContext.folderId` being set by the
 * panel that mounts the assistant.
 */

import type { AgentToolRegistry } from '../../../../core/ai/agent/AgentToolRegistry';
import { complianceEvaluateTool } from './complianceEvaluateTool';
import { complianceGapsTool } from './complianceGapsTool';
import { findRisksTool } from './findRisksTool';
import { flagControlGapsTool } from './flagControlGapsTool';
import { attachEvidenceTool } from './attachEvidenceTool';
import { recommendRemediationTool } from './recommendRemediationTool';
import { exportAuditBundleTool } from './exportAuditBundleTool';
import { reviewClauseTool } from './reviewClauseTool';
import { decideClauseProposalTool } from './decideClauseProposalTool';
import { setClauseSoaTool } from './setClauseSoaTool';

export function registerCompassAgentTools(registry: AgentToolRegistry): void {
  registry.register(complianceEvaluateTool);
  registry.register(complianceGapsTool);
  registry.register(findRisksTool);
  registry.register(flagControlGapsTool);
  registry.register(attachEvidenceTool);
  registry.register(recommendRemediationTool);
  registry.register(exportAuditBundleTool);
  // Per-clause resolution surface — agent parity with ClauseDetailDrawer.
  registry.register(reviewClauseTool);
  registry.register(decideClauseProposalTool);
  registry.register(setClauseSoaTool);
}

export {
  complianceEvaluateTool,
  complianceGapsTool,
  findRisksTool,
  flagControlGapsTool,
  attachEvidenceTool,
  recommendRemediationTool,
  exportAuditBundleTool,
  reviewClauseTool,
  decideClauseProposalTool,
  setClauseSoaTool,
};
